import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import styled, { createGlobalStyle } from "styled-components";
import { useTranslation } from "react-i18next";
import { Popover, Button, Input, Tooltip } from "antd";
import { SearchOutlined, CloseOutlined } from "@ant-design/icons";
import VirtualList from "rc-virtual-list";
import { useSelector } from "react-redux";
import { ReactComponent as CheckmarkIcon } from "../../assets/svg/check.svg";
import {
  WHITE,
  GRAY_50,
  GRAY_100,
  GRAY_200,
  GRAY_300,
  GRAY_400,
  GRAY_500,
  GRAY_700,
  PRIMARY_50,
  PRIMARY_200,
  PRIMARY_400,
  PRIMARY_600,
  PRIMARY_700,
  PRIMARY_800,
  BLACK
} from "../../constant/colors";
import {
  getStatusTagStyle,
  PATIENT_STATUS_COLORS,
  ENROLLMENT_STATUS_COLORS,
  CLAIM_STATUS_COLORS
} from "../../util/statusTagStyles";
import { selectUserId } from "../../store/selector";
import { UNASSIGNED_VALUE } from "../../constant/filters";
import { getActionCategory } from "./bulkActionUtils";

const GlobalPopoverStyles = createGlobalStyle`
  .bulk-action-popover.ant-popover {
    /* Ensure popover appears above the action bar content when attached to it */
    z-index: 1000 !important;
  }

  .bulk-action-popover.ant-popover .ant-popover-arrow {
    display: none !important;
    visibility: hidden !important;
  }

  .bulk-action-popover.ant-popover .ant-popover-inner-content {
    padding: 0 !important;
  }

  /* Hide native scrollbar, use rc-virtual-list's custom scrollbar */
  .bulk-action-popover .rc-virtual-list-holder {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
  }
  .bulk-action-popover .rc-virtual-list-holder::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
  }

  /* Keep holder-inner in normal flow so items contribute to auto-width
     calculation. Without this, rc-virtual-list uses position:absolute when
     virtualizing, which takes items out of flow and shrinks auto-width
     containers. transform:translateY() still works for scroll positioning. */
  .bulk-action-popover .rc-virtual-list-holder-inner {
    position: static !important;
  }

  /* rc-virtual-list scrollbar styling */
  .bulk-action-popover .rc-virtual-list-scrollbar {
    width: 10px !important;
    right: 3px !important; /* 3px from right edge */
    background: ${GRAY_100} !important; /* Gutter/track background */
    border-radius: 5px !important; /* Rounded corners */
    visibility: visible !important;
  }

  .bulk-action-popover .rc-virtual-list-scrollbar-thumb {
    background: ${GRAY_300} !important; /* Lighter gray thumb */
    border-radius: 5px !important;
    width: 10px !important;
  }

  .assignee-mode-tooltip.ant-tooltip .ant-tooltip-inner {
    background-color: ${WHITE};
    color: ${BLACK};
    box-shadow: 0px 0px 30px 0px rgba(0, 0, 0, 0.1);
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 400;
  }

  .assignee-mode-tooltip.ant-tooltip .ant-tooltip-arrow-content {
    --antd-arrow-background-color: ${WHITE};
    background-color: ${WHITE} !important;
  }

  .assignee-mode-tooltip.ant-tooltip .ant-tooltip-arrow-content::before {
    background: ${WHITE} !important;
  }
`;

const PopoverWrapper = styled.div``;

const DropdownContainer = styled.div`
  width: ${(props) => props.$width || "280px"};
`;

const DropdownHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${GRAY_100};
  font-weight: 600;
  color: ${GRAY_700};
`;

const DropdownContent = styled.div`
  padding: 0;
`;

const SearchContainer = styled.div`
  padding: 8px;
  border-bottom: 1px solid ${GRAY_200};
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  overflow: hidden;
`;

const ChipGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;

  .ant-input-affix-wrapper {
    border: none;
    padding: 0;
    box-shadow: none;
    background: transparent;
    &:hover,
    &:focus {
      border: none;
      box-shadow: none;
    }
  }

  .ant-input {
    font-size: 14px;
    background: transparent;
    &::placeholder {
      color: ${GRAY_300};
    }
  }

  .anticon-search {
    color: ${GRAY_500};
    font-size: 14px;
  }
`;

const Chip = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${GRAY_50};
  border: 1px solid ${GRAY_200};
  border-radius: 4px;
  padding: 2px 4px 2px 8px;
  max-width: 120px;
`;

const ChipText = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${GRAY_700};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChipCount = styled.div`
  background: ${GRAY_50};
  border: 1px solid ${GRAY_200};
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${GRAY_700};
`;

const SelectContainer = styled.div`
  max-height: ${(props) => `${props.$maxHeight || 280}px`};
  overflow: hidden;
`;

const OptionsList = styled.div`
  padding: 0;
`;

const OptionItem = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: ${(props) => (props.$selected ? PRIMARY_50 : "transparent")};

  &:hover {
    background: ${(props) => (props.$selected ? PRIMARY_50 : GRAY_50)};
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }
`;

const OptionTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
`;

const OptionLabel = styled.div`
  font-size: 14px;
  font-weight: ${(props) => (props.$bold ? "600" : "400")};
  color: ${BLACK};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OptionSubtext = styled.div`
  font-size: 12px;
  color: ${GRAY_500};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MeSuffix = styled.span`
  color: ${GRAY_400};
  font-weight: 400;
`;

const ModeToggleWrapper = styled.div`
  padding: 8px;
  border-top: 1px solid ${GRAY_200};
`;

const DropdownFooter = styled.div`
  padding: 8px;
  border-top: 1px solid ${GRAY_200};
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  align-items: center;
`;

const CancelButton = styled(Button)`
  && {
    color: ${PRIMARY_600};
    height: 29px;
    padding: 4px 8px;
    border: none;
    border-bottom: 1.5px solid ${PRIMARY_600};
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    transition: all 0.2s ease;

    &:hover {
      color: ${PRIMARY_700};
      background: linear-gradient(to bottom, rgba(21, 112, 239, 0), rgba(21, 112, 239, 0.2));
      border-bottom: 1.5px solid ${PRIMARY_700};
    }

    &:focus {
      color: ${PRIMARY_800};
      background: linear-gradient(to bottom, rgba(23, 92, 211, 0), rgba(23, 92, 211, 0.2));
      border-bottom: 1.5px solid ${PRIMARY_800};
      outline: none;
    }
  }
`;

const ConfirmButton = styled(Button)`
  && {
    height: 29px;
    min-width: 32px;
    padding: 4px 8px;
    border-radius: 8px;
    border: none;
    color: ${WHITE};
    background: ${PRIMARY_600};
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background: ${PRIMARY_800};
      color: ${WHITE};
    }

    &:focus:not(:disabled) {
      background: ${PRIMARY_600};
      color: ${WHITE};
      box-shadow: inset 0 0 0 2px ${PRIMARY_200};
      outline: none;
    }

    &:disabled {
      background: ${GRAY_300};
      color: ${WHITE};
    }
  }
`;

const ModeToggleContainer = styled.div`
  display: flex;
  border: 1px solid ${GRAY_200};
  border-radius: 8px;
  overflow: hidden;
`;

const ModeButton = styled(Button)`
  && {
    flex: 1;
    height: 32px;
    border: 1px solid ${(props) => (props.$active ? PRIMARY_400 : "transparent")} !important;
    border-radius: 0 !important;
    font-size: 12px;
    font-weight: 600;
    color: ${(props) => (props.$active ? PRIMARY_600 : GRAY_500)};
    background: ${(props) => (props.$active ? PRIMARY_50 : WHITE)};
    box-shadow: none !important;
    transition: none !important;

    &:not(:last-child) {
      border-right: ${(props) => (props.$active ? `1px solid ${PRIMARY_400}` : `1px solid ${GRAY_200}`)} !important;
    }

    &:hover,
    &:focus {
      color: ${(props) => (props.$active ? PRIMARY_600 : GRAY_700)};
      background: ${(props) => (props.$active ? PRIMARY_50 : GRAY_50)};
      transition: none !important;
    }
  }

  /* Apply rounded corners to first button when active */
  &&:first-child {
    border-radius: ${(props) => (props.$active ? "8px 0 0 8px" : "0")} !important;
  }

  /* Apply rounded corners to last button when active */
  &&:last-child {
    border-radius: ${(props) => (props.$active ? "0 8px 8px 0" : "0")} !important;
  }
`;

const StatusTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const NoDataMessage = styled.div`
  padding: 16px;
  text-align: left;
  color: ${GRAY_500};
  font-size: 14px;
`;

/**
 * Inline dropdown/popover for bulk action value selection.
 */
const BulkActionDropdown = ({
  visible,
  actionType,
  targetEntity,
  entityType,
  targetRef,
  options,
  onConfirm,
  onCancel,
  onSearch,
  popupContainerRef
}) => {
  const { t } = useTranslation();
  const currentUserId = useSelector(selectUserId);
  const [selectedValue, setSelectedValue] = useState(null);
  const [assigneeMode, setAssigneeMode] = useState("set"); // set, add, remove
  const [facilityMode, setFacilityMode] = useState("set"); // set, add, remove
  const [searchText, setSearchText] = useState("");
  // Stays true for 300ms after closing so the Popover can animate out,
  // but starts false so never-opened dropdowns don't flash content.
  const [shouldRenderContent, setShouldRenderContent] = useState(false);

  const category = getActionCategory(actionType);
  const isAssigneeCategory = category === "assignee" || category === "multi_assignee";
  const isFacilityCategory = category === "facility" || category === "multi_facility";
  const isMultiAssignee = category === "multi_assignee";
  const isMultiFacility = category === "multi_facility";
  const isPreferredLanguageCategory = category === "preferred_language";
  const isSearchable =
    isAssigneeCategory || isFacilityCategory || category === "physician" || isPreferredLanguageCategory;

  const resetState = useCallback(() => {
    setSelectedValue(null);
    setAssigneeMode("set");
    setFacilityMode("set");
    setSearchText("");
  }, []);

  React.useEffect(() => {
    if (visible) {
      setShouldRenderContent(true);
    } else {
      const timer = setTimeout(() => {
        resetState();
        setShouldRenderContent(false);
      }, 300); // Match Ant Design Popover animation duration
      return () => clearTimeout(timer);
    }
  }, [visible, resetState]);

  // Unassigned is only valid in Override; clear it when the user switches to Add/Remove.
  const handleAssigneeModeChange = useCallback((nextMode) => {
    setAssigneeMode(nextMode);
    if (nextMode !== "set") {
      setSelectedValue((prev) =>
        Array.isArray(prev) && prev.includes(UNASSIGNED_VALUE) ? prev.filter((v) => v !== UNASSIGNED_VALUE) : prev
      );
    }
  }, []);

  const handleCancel = useCallback(() => {
    // Close dropdown - state reset will be handled by useEffect
    onCancel();
  }, [onCancel]);

  const handleConfirm = useCallback(() => {
    // Multi-assignee Override with only "Unassigned" selected → dedicated clear action with no value.
    const isClearAssignees =
      isMultiAssignee &&
      assigneeMode === "set" &&
      Array.isArray(selectedValue) &&
      selectedValue.length === 1 &&
      selectedValue[0] === UNASSIGNED_VALUE;

    if (isClearAssignees) {
      onConfirm({
        actionType: "assignees_clear",
        targetEntity,
        value: undefined
      });
      resetState();
      return;
    }

    const finalActionType = isMultiAssignee
      ? `assignees_${assigneeMode}`
      : isMultiFacility
      ? `facilities_${facilityMode}`
      : actionType;

    let finalValue = selectedValue;
    if (isMultiAssignee && Array.isArray(selectedValue)) {
      finalValue = selectedValue.map((id) => {
        const option = options.find((opt) => opt.value === id);
        return { id: option.value, type: option.item?.type || "user" };
      });
    } else if (isMultiFacility && Array.isArray(selectedValue)) {
      finalValue = selectedValue.map((id) => {
        const option = options.find((opt) => opt.value === id);
        return { id: option.value, type: option.item?.type || "facility" };
      });
    } else if (category === "assignee" && selectedValue) {
      const option = options.find((opt) => opt.value === selectedValue);
      finalValue = { id: option.value, type: option.item?.type || "user" };
    }

    onConfirm({
      actionType: finalActionType,
      targetEntity,
      value: finalValue
    });
    resetState();
  }, [
    actionType,
    targetEntity,
    category,
    isMultiAssignee,
    assigneeMode,
    isMultiFacility,
    facilityMode,
    selectedValue,
    options,
    onConfirm,
    resetState
  ]);

  const handleSearch = useCallback(
    (value) => {
      setSearchText(value);
      if (onSearch) {
        onSearch(value);
      }
    },
    [onSearch]
  );

  const isConfirmDisabled = selectedValue === null || (Array.isArray(selectedValue) && selectedValue.length === 0);

  const filteredOptions = useMemo(() => {
    let result = options;
    // Unassigned is only meaningful for multi-assignee Override (patients).
    // Hide it for single-assignee pickers (enrollment/claim) and for Add/Remove modes.
    const showUnassigned = isMultiAssignee && assigneeMode === "set";
    if (isAssigneeCategory && !showUnassigned) {
      result = result.filter((opt) => opt.value !== UNASSIGNED_VALUE);
    }
    if (searchText) {
      const lowerSearch = searchText.toLowerCase();
      result = result.filter((opt) => opt.label?.toLowerCase().includes(lowerSearch));
    }
    if (isAssigneeCategory) {
      result = [...result].sort((a, b) => {
        const rank = (opt) => {
          if (opt.value === currentUserId) return 0;
          if (opt.value === UNASSIGNED_VALUE) return 1;
          return 2;
        };
        return rank(a) - rank(b);
      });
    }
    return result;
  }, [options, searchText, isAssigneeCategory, isMultiAssignee, assigneeMode, currentUserId]);

  const toggleMultiSelect = useCallback((value) => {
    setSelectedValue((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      if (current.includes(value)) {
        return current.filter((v) => v !== value);
      }
      // Unassigned is mutually exclusive with real assignees in multi-select.
      if (value === UNASSIGNED_VALUE) return [UNASSIGNED_VALUE];
      return [...current.filter((v) => v !== UNASSIGNED_VALUE), value];
    });
  }, []);

  const handleSearchKeyDown = useCallback(
    (e) => {
      // When backspace is pressed and search field is empty, remove the rightmost selected assignee
      if (e.key === "Backspace" && searchText === "" && Array.isArray(selectedValue) && selectedValue.length > 0) {
        e.preventDefault();
        const lastValue = selectedValue[selectedValue.length - 1];
        toggleMultiSelect(lastValue);
      }
    },
    [searchText, selectedValue, toggleMultiSelect]
  );

  const selectedOptions = useMemo(() => {
    if (!Array.isArray(selectedValue)) return [];
    return options.filter((opt) => selectedValue.includes(opt.value));
  }, [options, selectedValue]);

  const renderModeToggle = (modeType, currentMode, setMode) => (
    <ModeToggleWrapper>
      <ModeToggleContainer>
        <Tooltip
          title={t(`bulk_actions.${modeType}_mode.override_tooltip`)}
          placement="top"
          overlayClassName="assignee-mode-tooltip"
        >
          <ModeButton
            $active={currentMode === "set"}
            onClick={() => setMode("set")}
            data-testid={`${modeType}-mode-set`}
            type="text"
          >
            {t(`bulk_actions.${modeType}_mode.override`)}
          </ModeButton>
        </Tooltip>
        <Tooltip
          title={t(`bulk_actions.${modeType}_mode.add_tooltip`)}
          placement="top"
          overlayClassName="assignee-mode-tooltip"
        >
          <ModeButton
            $active={currentMode === "add"}
            onClick={() => setMode("add")}
            data-testid={`${modeType}-mode-add`}
            type="text"
          >
            {t(`bulk_actions.${modeType}_mode.add`)}
          </ModeButton>
        </Tooltip>
        <Tooltip
          title={t(`bulk_actions.${modeType}_mode.remove_tooltip`)}
          placement="top"
          overlayClassName="assignee-mode-tooltip"
        >
          <ModeButton
            $active={currentMode === "remove"}
            onClick={() => setMode("remove")}
            data-testid={`${modeType}-mode-remove`}
            type="text"
          >
            {t(`bulk_actions.${modeType}_mode.remove`)}
          </ModeButton>
        </Tooltip>
      </ModeToggleContainer>
    </ModeToggleWrapper>
  );

  const renderAssigneeModeToggle = () => renderModeToggle("assignee", assigneeMode, handleAssigneeModeChange);

  const renderFacilityModeToggle = () => renderModeToggle("facility", facilityMode, setFacilityMode);

  const renderSearchableSelect = () => {
    const placeholder = (() => {
      if (category === "physician") return "Physician Name";
      if (isPreferredLanguageCategory) return t("bulk_actions.dropdown.search_language");
      return t(
        `bulk_actions.actions.${isAssigneeCategory ? "assignee" : isFacilityCategory ? "facility" : actionType}`
      );
    })();

    return (
      <>
        <SearchContainer>
          {(isMultiAssignee || isMultiFacility) && selectedOptions.length > 0 && (
            <ChipGroup>
              <Chip>
                <ChipText>{selectedOptions[0].label}</ChipText>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined style={{ fontSize: "10px" }} />}
                  onClick={() => toggleMultiSelect(selectedOptions[0].value)}
                  style={{ padding: 0, width: "16px", height: "16px" }}
                />
              </Chip>
              {selectedOptions.length > 1 && <ChipCount>+{selectedOptions.length - 1}</ChipCount>}
            </ChipGroup>
          )}
          <SearchInputWrapper>
            <Input
              placeholder={placeholder}
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              suffix={<SearchOutlined />}
              data-testid="action-search-input"
            />
          </SearchInputWrapper>
        </SearchContainer>
        <SelectContainer>
          {filteredOptions.length === 0 ? (
            <NoDataMessage>
              {(() => {
                if (isAssigneeCategory) {
                  return t("bulk_actions.dropdown.no_matching_assignees");
                } else if (isFacilityCategory) {
                  return t("bulk_actions.dropdown.no_matching_facilities");
                } else if (category === "physician") {
                  return t("bulk_actions.dropdown.no_matching_physicians");
                } else if (isPreferredLanguageCategory) {
                  return t("bulk_actions.dropdown.no_matching_languages");
                }
              })()}
            </NoDataMessage>
          ) : (
            <OptionsList data-testid="action-options-list">
              <VirtualList
                data={filteredOptions}
                height={280}
                fullHeight={false}
                itemHeight={category === "physician" ? 60 : 44}
                itemKey="value"
              >
                {(option) => {
                  const isSelected =
                    isMultiAssignee || isMultiFacility
                      ? Array.isArray(selectedValue) && selectedValue.includes(option.value)
                      : selectedValue === option.value;

                  const subtextParts = [];
                  if (category === "physician" && option.item) {
                    if (option.item.npi) subtextParts.push(`NPI: ${option.item.npi}`);
                    if (option.item.address) subtextParts.push(option.item.address);
                  }

                  const isCurrentUser = (isAssigneeCategory || isFacilityCategory) && option.value === currentUserId;

                  const optionDomId = (() => {
                    if (!isAssigneeCategory) return undefined;
                    if (option.value === currentUserId) return "bulk_action_assignee_me";
                    if (option.value === UNASSIGNED_VALUE) return "bulk_action_assignee_unassigned";
                    return undefined;
                  })();

                  return (
                    <OptionItem
                      $selected={isSelected}
                      onClick={() =>
                        isMultiAssignee || isMultiFacility
                          ? toggleMultiSelect(option.value)
                          : setSelectedValue(option.value)
                      }
                      data-testid={`option-item-${option.value}`}
                      id={optionDomId}
                    >
                      <OptionTextContainer>
                        <OptionLabel $bold={isSelected || category === "physician"}>
                          {option.label}
                          {isCurrentUser && <MeSuffix> {t("bulk_actions.current_user_suffix")}</MeSuffix>}
                        </OptionLabel>
                        {subtextParts.length > 0 && <OptionSubtext>{subtextParts.join(", ")}</OptionSubtext>}
                      </OptionTextContainer>
                      {(isMultiAssignee || isMultiFacility) && isSelected && (
                        <CheckmarkIcon style={{ width: "12px", height: "12px", color: PRIMARY_600 }} />
                      )}
                    </OptionItem>
                  );
                }}
              </VirtualList>
            </OptionsList>
          )}
        </SelectContainer>
      </>
    );
  };

  const renderOptionsList = () => {
    const STATUS_COLOR_MAPS = {
      patient: PATIENT_STATUS_COLORS,
      enrollment: ENROLLMENT_STATUS_COLORS,
      claim: CLAIM_STATUS_COLORS
    };
    const statusEntity = targetEntity || entityType;
    const statusColorMap = STATUS_COLOR_MAPS[statusEntity] || PATIENT_STATUS_COLORS;

    const isStatusAction = actionType === "status";

    return (
      <SelectContainer>
        <OptionsList data-testid="action-options-list">
          <VirtualList data={options} height={280} fullHeight={false} itemHeight={44} itemKey="value">
            {(option) => (
              <OptionItem
                $selected={selectedValue === option.value}
                onClick={() => setSelectedValue(option.value)}
                data-testid={`option-item-${option.value}`}
              >
                <OptionTextContainer>
                  <OptionLabel $bold={selectedValue === option.value}>
                    {isStatusAction ? (
                      <StatusTag style={getStatusTagStyle(option.label, statusColorMap)}>{option.label}</StatusTag>
                    ) : (
                      option.label
                    )}
                  </OptionLabel>
                </OptionTextContainer>
              </OptionItem>
            )}
          </VirtualList>
        </OptionsList>
      </SelectContainer>
    );
  };

  const getDropdownTitle = () => {
    const actionKey = actionType.replace("_set", "");
    return t(`bulk_actions.actions.${actionKey}`);
  };

  const renderActionContent = () => {
    if (isSearchable) {
      return renderSearchableSelect();
    }
    return renderOptionsList();
  };

  const renderContent = () => {
    return (
      <DropdownContainer $width={category === "status" ? "200px" : undefined}>
        {!isSearchable && category !== "status" && <DropdownHeader>{getDropdownTitle()}</DropdownHeader>}
        <DropdownContent>
          {renderActionContent()}
          {(isMultiAssignee && renderAssigneeModeToggle()) || (isMultiFacility && renderFacilityModeToggle())}
        </DropdownContent>
        <DropdownFooter>
          <CancelButton
            type="link"
            onClick={handleCancel}
            data-testid="dropdown-cancel"
            data-pendo="bulk_action.cancel"
          >
            {t("bulk_actions.dropdown.cancel")}
          </CancelButton>
          <ConfirmButton
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            data-testid="dropdown-confirm"
            data-pendo="bulk_action.confirm"
          >
            {t("bulk_actions.dropdown.confirm")}
          </ConfirmButton>
        </DropdownFooter>
      </DropdownContainer>
    );
  };

  return (
    <>
      <GlobalPopoverStyles />
      <PopoverWrapper>
        <Popover
          open={visible}
          content={shouldRenderContent ? renderContent() : null}
          trigger="click"
          placement="top"
          arrow={false}
          destroyTooltipOnHide
          overlayClassName="bulk-action-popover"
          overlayInnerStyle={{
            borderRadius: "8px",
            boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)"
          }}
          // Attach popover to the fixed action bar to prevent scroll issues
          getPopupContainer={() => popupContainerRef?.current || document.body}
          // Prevent automatic repositioning when dropdown would extend beyond container
          autoAdjustOverflow={false}
        >
          {targetRef}
        </Popover>
      </PopoverWrapper>
    </>
  );
};

BulkActionDropdown.propTypes = {
  visible: PropTypes.bool,
  actionType: PropTypes.string.isRequired,
  targetEntity: PropTypes.string,
  entityType: PropTypes.oneOf(["patient", "enrollment", "claim"]).isRequired,
  targetRef: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string
    })
  ),
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSearch: PropTypes.func,
  popupContainerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
};

BulkActionDropdown.defaultProps = {
  visible: false,
  targetEntity: null,
  options: [],
  targetRef: null,
  onSearch: null,
  popupContainerRef: null
};

export default BulkActionDropdown;
